import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import api from '../../../../services/apiConfig';
import useDebounce from '../../../../hooks/useDebounce';
import styles from './ProductSearchInput.module.css';

interface Client {
    ID: number;
    NOME: string;
    DOCUMENTO: string;
    [key: string]: any;
}

interface ClientSearchInputProps {
    onSelect: (client: Client) => void;
    placeholder?: string;
    initialValue?: string;
}

const ClientSearchInput: React.FC<ClientSearchInputProps> = ({ onSelect, placeholder = "Buscar cliente...", initialValue = "" }) => {
    const [searchTerm, setSearchTerm] = useState(initialValue);
    const [results, setResults] = useState<Client[]>([]);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
    
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const isFocusedRef = useRef(false);

    useEffect(() => {
        const searchClients = async () => {
            if (debouncedSearchTerm.length < 2) {
                setResults([]);
                return;
            }

            setLoading(true);
            try {
                const response = await api.get('/clients', {
                    params: { 
                        search: debouncedSearchTerm,
                        limit: 10,
                        page: 1
                    }
                });
                
                if (response.data && Array.isArray(response.data.clients)) {
                     setResults(response.data.clients);
                     setSelectedIndex(-1); // Reset selected index on new search
                     if (isFocusedRef.current) {
                         setIsOpen(true);
                     }
                }
            } catch (error) {
                console.error("Erro ao buscar clientes:", error);
            } finally {
                setLoading(false);
            }
        };

        searchClients();
    }, [debouncedSearchTerm]);

    React.useLayoutEffect(() => {
        const updatePosition = () => {
            if (isOpen && wrapperRef.current) {
                const rect = wrapperRef.current.getBoundingClientRect();
                const spaceBelow = window.innerHeight - rect.bottom;
                const dropdownHeight = 300; 
                
                setDropdownStyle({
                    position: 'fixed',
                    top: spaceBelow < dropdownHeight && rect.top > dropdownHeight ? 'auto' : rect.bottom + 8,
                    bottom: spaceBelow < dropdownHeight && rect.top > dropdownHeight ? window.innerHeight - rect.top + 8 : 'auto',
                    left: rect.left,
                    right: 'auto',
                    width: rect.width,
                    zIndex: 99999
                });
            }
        };

        if (isOpen) {
            updatePosition();
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
        }

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                const target = event.target as Element;
                if (!target.closest(`.${styles.dropdown}`)) {
                    setIsOpen(false);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (client: Client) => {
        onSelect(client);
        setSearchTerm(client.NOME);
        setIsOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                if (results.length > 0) setIsOpen(true);
            }
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((prev) => {
                const nextIndex = prev < results.length - 1 ? prev + 1 : prev;
                scrollToIndex(nextIndex);
                return nextIndex;
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((prev) => {
                const nextIndex = prev > 0 ? prev - 1 : prev;
                scrollToIndex(nextIndex);
                return nextIndex;
            });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedIndex >= 0 && selectedIndex < results.length) {
                handleSelect(results[selectedIndex]);
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    const scrollToIndex = (index: number) => {
        if (!listRef.current) return;
        const item = listRef.current.children[index] as HTMLElement;
        if (item) {
            item.scrollIntoView({ block: 'nearest' });
        }
    };

    return (
        <div className={styles.searchContainer} ref={wrapperRef}>
            <input
                type="text"
                className={styles.searchInput}
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (!isOpen && e.target.value.length >= 2) setIsOpen(true);
                }}
                onFocus={() => {
                    isFocusedRef.current = true;
                    if (searchTerm.length >= 2 && results.length > 0) {
                        setIsOpen(true);
                    }
                }}
                onBlur={() => {
                    isFocusedRef.current = false;
                }}
                onKeyDown={handleKeyDown}
            />
            
            {isOpen && ReactDOM.createPortal(
                <div className={styles.dropdown} style={{ ...dropdownStyle, maxHeight: '300px' }} ref={listRef}>
                    {results.length > 0 ? (
                        results.map((client, index) => (
                            <div 
                                key={client.ID} 
                                className={styles.dropdownItem}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSelect(client);
                                }}
                                onClick={() => handleSelect(client)}
                                style={{ backgroundColor: index === selectedIndex ? '#dbeafe' : undefined }}
                            >
                                <span className={styles.productRef}>{client.ID}</span>
                                <span className={styles.productDesc}>{client.NOME}</span>
                                <div style={{ fontSize: '0.8rem', color: '#888' }}>{client.DOCUMENTO} - {client.CIDADE}/{client.ESTADO}</div>
                            </div>
                        ))
                    ) : !loading && debouncedSearchTerm.length >= 2 ? (
                        <div className={styles.dropdownItem} style={{ cursor: 'default', color: '#888' }}>
                            Nenhum cliente encontrado.
                        </div>
                    ) : null}
                </div>,
                document.body
            )}
        </div>
    );
};

export default ClientSearchInput;